import { useEffect, type ReactNode } from "react";
import {
  useAuthLoading,
  useIsAuthenticated,
  useLogin,
} from "@/auth/AuthProvider";

/**
 * Test-only escape hatch. When ``localStorage.__northstar_test_auth_bypass__``
 * is "1", the gate short-circuits without ever asking MSAL — Playwright sets
 * it before navigation so e2e flows aren't held up by the Entra redirect.
 *
 * Stripped from production builds: ``import.meta.env.PROD`` is statically
 * replaced by Vite at build time, so this whole branch becomes
 * ``if (false) { ... }`` and tree-shakes away. An attacker who somehow
 * sets the localStorage flag on a prod build sees no effect.
 */
function isTestBypassEnabled(): boolean {
  if (import.meta.env.PROD) return false;
  try {
    return typeof window !== "undefined"
      && window.localStorage.getItem("__northstar_test_auth_bypass__") === "1";
  } catch {
    return false;
  }
}

/**
 * Wraps the app shell. Triggers a login redirect when no account is present;
 * otherwise renders children. Loading state covers both MSAL initialization
 * and the post-redirect handshake.
 */
export function AuthGate({ children }: { children: ReactNode }) {
  const isAuthed = useIsAuthenticated();
  const loading = useAuthLoading();
  const login = useLogin();
  const testBypass = isTestBypassEnabled();

  useEffect(() => {
    if (testBypass) return;
    if (!loading && !isAuthed) {
      login();
    }
  }, [loading, isAuthed, login, testBypass]);

  if (testBypass) {
    return <>{children}</>;
  }

  if (loading || !isAuthed) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-white">
        <span className="font-sans text-sm text-bw-ink-muted">Signing in…</span>
      </div>
    );
  }

  return <>{children}</>;
}
